/**
 * Analytics Service
 * Main service for tracking user analytics
 */

import { store } from '../../../app/store/store';
import { 
  addEventToQueue, 
  initializeSession, 
  updateLastActivity,
  incrementPageViews,
  incrementInteractions,
  sendAnalyticsEvents 
} from '../../../app/store/slices/analyticsSlice';
import { EVENT_TYPES } from '../utils/eventTypes';
import EventBatcher from './EventBatcher';
import StorageManager from './StorageManager';

class AnalyticsService {
  constructor() {
    this.isInitialized = false;
    this.sessionId = null;
    this.userId = null;
    this.batcher = null;
    this.storage = null;
    this.sessionStartTime = null;
    this.pageStartTime = null;
    this.currentPath = null;
  }

  async init() {
    if (this.isInitialized) return;

    try {
      // Initialize storage and batcher
      this.storage = new StorageManager();
      this.batcher = new EventBatcher();
      
      // Generate session ID
      this.sessionId = this.generateSessionId();
      this.sessionStartTime = Date.now();
      
      // Initialize session in Redux
      store.dispatch(initializeSession({
        sessionId: this.sessionId,
        startTime: this.sessionStartTime
      }));

      // Start batcher
      this.batcher.start();
      
      // Set up page visibility tracking
      this.setupPageVisibilityTracking();
      
      // Set up unload tracking
      this.setupUnloadTracking();
      
      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize analytics service:', error);
    }
  }

  setUser(userId, userMetadata = {}) {
    this.userId = userId;
    this.track(EVENT_TYPES.SESSION_START, {
      userId,
      ...userMetadata
    });
  }

  track(eventName, properties = {}) {
    if (!this.isInitialized) {
      return;
    }

    const event = {
      eventName,
      properties: {
        ...properties,
        userId: this.userId,
        sessionId: this.sessionId,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        path: window.location.pathname,
        userAgent: navigator.userAgent,
        referrer: document.referrer
      }
    };

    // Add to Redux queue
    store.dispatch(addEventToQueue(event));
    store.dispatch(updateLastActivity());

    // Add to batcher
    if (this.batcher) {
      this.batcher.addEvent(event);
    }

    if (eventName === EVENT_TYPES.PAGE_VIEW) {
      store.dispatch(incrementPageViews());
    } else if (eventName === EVENT_TYPES.USER_INTERACTION) {
      store.dispatch(incrementInteractions());
    }
  }

  trackPageView(path, additionalData = {}) {
    if (this.currentPath && this.pageStartTime) {
      const timeSpent = Date.now() - this.pageStartTime;
      this.track(EVENT_TYPES.PAGE_EXIT, {
        path: this.currentPath,
        timeSpent,
        exitType: 'navigation'
      });
    }

    this.currentPath = path;
    this.pageStartTime = Date.now();
    
    this.track(EVENT_TYPES.PAGE_VIEW, {
      path,
      ...additionalData
    });
  }

  trackInteraction(action, element, additionalData = {}) {
    this.track(EVENT_TYPES.USER_INTERACTION, {
      action,
      element,
      ...additionalData
    });
  }

  trackError(error, context = {}) {
    this.track(EVENT_TYPES.ERROR, {
      message: error.message,
      stack: error.stack,
      context,
      severity: 'error'
    });
  }

  async flushEvents() {
    if (this.batcher) {
      await this.batcher.forceFlush();
    }
  }

  setupPageVisibilityTracking() {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.track(EVENT_TYPES.PAGE_HIDDEN, {
          path: window.location.pathname
        });
      } else {
        this.track(EVENT_TYPES.PAGE_VISIBLE, {
          path: window.location.pathname
        });
      }
    });
  }

  setupUnloadTracking() {
    window.addEventListener('beforeunload', () => {
      const sessionDuration = Date.now() - this.sessionStartTime;
      this.track(EVENT_TYPES.SESSION_END, {
        sessionDuration,
        lastPath: window.location.pathname
      });
      
      if (this.batcher) {
        this.batcher.flush(true);
      }
    });
  }

  generateSessionId() {
    return Date.now() + '-' + Math.random().toString(36).substr(2, 9);
  }

  getMetrics() {
    const state = store.getState();
    return {
      sessionId: this.sessionId,
      sessionDuration: Date.now() - this.sessionStartTime,
      pendingEvents: state.analytics.eventQueue.length,
      eventsSent: state.analytics.eventsSent,
      errorsOccurred: state.analytics.eventsError,
      pageViews: state.analytics?.sessionData.pageViews,
      interactions: state.analytics?.sessionData.interactionCount,
      batcherStatus: this.batcher ? this.batcher.getStatus() : null
    };
  }

  // Stop the service
  stop() {
    if (this.batcher) {
      this.batcher.stop();
    }
    this.isInitialized = false;
  }
}

export default new AnalyticsService();
